import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import {
  PriceDetailCardDiscount, PriceDetailCardPrice, PriceDetailCardSubtitle
} from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import { TagMemberType } from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { mapRooms, multipleGuest, multipleTypePassengers } from '@shared/payment-detail/mappers/price-card-info.mapper';
import { GuestRoom } from '@shared/base-search-bar/entities/room.entities';
import { AppFacade } from '@app/app.facade';
import { environment as ENV } from '@environment';
import { Countries } from '@ux/entities/country.enum';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Injectable()
export class PriceDetailService {

  constructor(
    private readonly appFacade: AppFacade,
    private readonly transloco: TranslocoService,
    private readonly currencyFormat: CurrencyFormatPipe
  ) { }

  public getTagMemberType$(isStrikethrough: boolean): Observable<TagMemberType> {
    return this.appFacade.isPrime$.pipe(
      map(isPrime => {
        const defaultTagType = isStrikethrough ? TagMemberType.Pay : TagMemberType.Rappi;
        return isPrime ? TagMemberType.Pro : defaultTagType;
      })
    );
  }

  public getCardDiscount$(
    product: SearchBy, discountAmount: number, discountPercentage: number, currencyCode: string
  ): Observable<PriceDetailCardDiscount> {
    return combineLatest([
      this.getTagMemberType$(false), this.appFacade.isPrime$
    ]).pipe(
      map(([priceCardTag, isPrime]) => {
        return discountAmount && discountPercentage ? {
          Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.DISCOUNTS'),
          Amount: `-${this.currencyFormat.transform(discountAmount, currencyCode)}`,
          UseTagStyle: this.getUseTagStyle(product, isPrime),
          TagMemberLabel: this.transloco.translate('PRICE_OFF', { percentage: discountPercentage }),
          TagMemberType: priceCardTag,
        } : null;
      })
    );
  }

  public getCardPricePerNightOrHoursItem(isByhours: boolean, price: number, currencyCode: string): PriceDetailCardPrice {
    const label = isByhours ? 'BOOKING_MODULE.PAYMENT_DETAIL.PRICE_PER_HOUR' : 'BOOKING_MODULE.PAYMENT_DETAIL.PRICE_PER_NIGHT';
    return {
      Label: this.transloco.translate(label),
      Amount: this.currencyFormat.transform(price, currencyCode),
    };
  }

  public getCardAmountPerNightOrHoursItem(
    isByhours: boolean, nights: number, guests: number, hours: number, capacity: number, amount: number, currencyCode: string
  ): PriceDetailCardPrice {
    const label = isByhours ? (
      capacity > 1 ? 'BOOKING_MODULE.PAYMENT_DETAIL.HOURS_PER_GUESTS' : 'BOOKING_MODULE.PAYMENT_DETAIL.HOURS_PER_GUEST'
    ) : 'BOOKING_MODULE.PAYMENT_DETAIL.NIGHTS_PER_GUEST';
    return {
      Label: this.transloco.translate(label, { nights: nights, guest: guests, hours: hours }),
      Amount: this.currencyFormat.transform(amount, currencyCode),
    };
  }

  public getCardGenericPaymentDetailSection(
    amount: number, taxes: number, fees: number, currencyCode: string): PriceDetailCardPrice[] {
    return [
      {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.FARE'),
        Amount: this.currencyFormat.transform(amount, currencyCode)
      },
      {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.TAX'),
        Amount: this.currencyFormat.transform(taxes, currencyCode),
        Disabled: !taxes
      },
      !!fees && {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.CHARGES'),
        Amount: this.currencyFormat.transform(fees, currencyCode),
        Info: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.PAYMENT_DETAIL_DESCRIPTION')
      },
    ];
  }

  public getCardExtendedPaymentDetailSection(
    taxes: number, fees: number, surcharges: number, commisionAmount: number, currencyCode: string
  ): PriceDetailCardPrice[] {
    return [
      {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.TAX'),
        Amount: this.currencyFormat.transform(taxes, currencyCode),
        Disabled: !taxes
      },
      !!fees && {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.CHARGES'),
        Amount: this.currencyFormat.transform(fees, currencyCode),
        Info: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.PAYMENT_DETAIL_DESCRIPTION')
      },
      !!surcharges && {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.SURCHARGES'),
        Amount: this.currencyFormat.transform(surcharges, currencyCode)
      },
      !!commisionAmount && {
        Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.COMMISSION'),
        Amount: this.currencyFormat.transform(commisionAmount, currencyCode)
      },
    ];
  }

  public getCardSubtitlesByPassengers(mapPassengerArrengement: PassengerArrangement[]): PriceDetailCardSubtitle[] {
    let description = '';
    mapPassengerArrengement?.forEach((passenger, index) => {
      const isLast = mapPassengerArrengement.length - 1 === index;
      const translateKey = multipleTypePassengers(passenger) ? 'BOOKING_MODULE.ITINERARY_PASSENGERS.MULTIPLE' :
        'BOOKING_MODULE.ITINERARY_PASSENGERS.SINGLE';
      description = `${description}${passenger.quantity} ${this.transloco.translate(`${translateKey}.${passenger.id}`)}`;
      description = !isLast ? `${description} - ` : description;
    });
    return [{
      Label: this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.PASSENGER'),
      Description: description
    }];
  }

  public getCardSubtitlesByGuests(
    mapGuestsPerRoom: GuestRoom[], isByhours: boolean, capacity: number
  ): PriceDetailCardSubtitle[] {
    let roomLabel = '';
    let description = '';
    if (isByhours) {
      roomLabel = this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.ROOM', { room: 1 });
      description = `${capacity} ${this.transloco.translate(capacity > 1 ? 'GUESTS' : 'GUEST')}`;
      return [{
        Label: roomLabel,
        Description: description
      }];
    }
    return mapGuestsPerRoom?.map((room, i) => {
      description = '';
      roomLabel = this.transloco.translate('BOOKING_MODULE.PAYMENT_DETAIL.ROOM', { room: i + 1 });
      const guestsPerRoom = mapRooms(room);
      guestsPerRoom?.forEach((guest, j) => {
        const isLastGuest = guestsPerRoom.length - 1 === j;
        const guestPrefix = guest.quantity;
        const hasMultipleGuest = multipleGuest(guest);
        const translateKey = hasMultipleGuest 
          ? `BOOKING_MODULE.ITINERARY_PASSENGERS.MULTIPLE.${guest.id}`
          : `BOOKING_MODULE.ITINERARY_PASSENGERS.SINGLE.${guest.id}`;
      
        description = `${description}${guestPrefix} ${this.transloco.translate(translateKey)}`;
        description = !isLastGuest ? `${description} - ` : description;
      });
      return {
        Label: roomLabel,
        Description: description
      };
    });
  }

  public getUseTagStyle(product: SearchBy, isPrime: boolean): boolean {
    return (product === SearchBy.FLIGHTS_BY_SECTOR || product === SearchBy.FLIGHTS) &&
      isPrime && ENV.countryInfo.iso_code === Countries.Colombia;
  }

}
